import { useEffect, useImperativeHandle, useState } from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { YearPicker } from "./YearPicker";
import {
  type YearPickerDataProps,
  yearPickerDataSchema,
} from "./YearPicker.schema";
import { YearPickerContent } from "./YearPickerContent";
import { YearPickerTrigger } from "./YearPickerTrigger";
import { YearPickerValue } from "./YearPickerValue";
import { YEAR_PICKER_TAG_NAME } from "./constants";

export interface YearPickerHandle {
  value: (value: number | null) => void;
  disabled: (value?: boolean) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [YEAR_PICKER_TAG_NAME]: {
      props: YearPickerDataProps;
      handle: YearPickerHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-year-picker": HTMLElement & YearPickerHandle;
  }
}

export function RWCYearPicker({
  ref,
  value: initialValue,
  disabled: initialDisabled,
  placeholder,
  min,
  max,
  arrow,
  size,
  layout,
}: YearPickerDataProps & { ref?: Ref<YearPickerHandle> }) {
  const host = useIslandHost();

  const [value, setValue] = useState<number | null>(initialValue ?? null);
  const [disabled, setDisabled] = useState(initialDisabled ?? false);

  useImperativeHandle(
    ref,
    () => ({
      value: setValue,
      disabled: (nextDisabled = true) => setDisabled(nextDisabled),
    }),
    [],
  );

  useEffect(() => {
    getElementInternals(host)?.setFormValue(
      disabled || null === value ? null : String(value),
    );
  }, [host, value, disabled]);

  return (
    <YearPicker
      value={value}
      onValueChange={setValue}
      disabled={disabled}
      min={min}
      max={max}
      arrow={arrow}
      size={size}
      layout={layout}
    >
      <YearPickerTrigger>
        <YearPickerValue placeholder={placeholder} />
      </YearPickerTrigger>
      <YearPickerContent />
    </YearPicker>
  );
}

componentManager.register(
  rwc(YEAR_PICKER_TAG_NAME, {
    component: RWCYearPicker,
    dataSchema: yearPickerDataSchema,
    formAssociated: true,
  }),
);
